import { Box, Typography, Button, TextField, CircularProgress } from '@mui/material'
import { styled } from '@mui/system'
import { createFileRoute, useParams, useNavigate, useLocation } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { RadioButtonUncheckedOutlined, RadioButtonCheckedOutlined, CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material'
import TimerDisplay from '../../components/TimerDisplay'
import NoData from 'icons/NoData.icon'
import axios from 'axios'

export const Route = createFileRoute('/(dashboard)/_dashboard/questions/$id')({
  component: RouteComponent,
})

const OptionContainer = styled(Box)<{ checked: boolean }>(({ checked, theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: theme.breakpoints.down('md') ? '12px 16px' : '16px 24px',
  borderRadius: '12px',
  border: !checked ? '1px solid #F0F1F3' : '2px solid #F0F1F3',
  borderColor: checked ? 'var(--Interviewer-Green)' : '#E8E8E8',
  background: '#FFFFFF',
  marginBottom: '12px',
  cursor: 'pointer',
  boxShadow: '0px 1px 2px 0px #0205111A',
  '&:hover': {
    borderColor: checked ? 'var(--Interviewer-Green)' : '#C8C8C8',
  },
}));

interface QuestionOptionProps {
  text: string;
  checked: boolean;
  onChange: () => void;
  type: 'radio' | 'checkbox';
  disabled?: boolean;
}

const QuestionOption = ({ text, checked, onChange, type, disabled }: QuestionOptionProps) => {
  const textColor = checked ? '#1A1A1A' : '#1A1A1A';

  const renderIcon = () => {
    if (type === 'radio') {
      return checked ? (
        <RadioButtonCheckedOutlined sx={{ color: 'var(--Interviewer-Green)' }} />
      ) : (
        <RadioButtonUncheckedOutlined sx={{ color: 'var(--Light-Gray)' }} />
      );
    } else { // checkbox
      return checked ? (
        <CheckBox sx={{ color: 'var(--Interviewer-Green)' }} />
      ) : (
        <CheckBoxOutlineBlank sx={{ color: 'var(--Light-Gray)' }} />
      );
    }
  };

  return (
    <OptionContainer checked={checked} onClick={onChange}>
      {renderIcon()}
      <Typography variant="body1" sx={{ color: textColor }}>
        {text}
      </Typography>
    </OptionContainer>
  );
};

const StyledContainer = styled(Box)(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',
    // margin: '32px',
    padding: '48px',
    background: '#ffffff',
    borderRadius: '24px',
    border: '1px solid var(--Light-Gray)',
    boxShadow: '0px 1px 2px 0px #0205111A',
    maxWidth: '835px',
    width: '100%',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      padding: '24px',
    },
}));

interface AnswerOption {
  answer: string;
  correct: boolean;
}

interface Question {
  submittedQuestionId: string;
  structure: 1 | 2 | 3;
  text: string;
  suggestedAnswers?: AnswerOption[];
}

interface Category {
  id: string;
  name: string;
  description: string;
  questions: Question[];
}

interface CategoryResult {
  submittedCategoryId: string;
  submittedCategoryName: string;
  correctAnswersCount: number;
}

interface InterviewResults {
  totalTime: number;
  categoryResults: CategoryResult[];
}

function RouteComponent() {
  const { id } = useParams({ from: '/(dashboard)/_dashboard/questions/$id' })
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [currentQuestionPhase, setCurrentQuestionPhase] = useState<'reading' | 'answering'>('reading');
  const [displayTimer, setDisplayTimer] = useState(0);
  const [readTimeDuration, setReadTimeDuration] = useState(() => {
    const navReadTime = (location.state as any)?.readTime;
    return navReadTime
  });

  const [phaseStartTime, setPhaseStartTime] = useState<number | null>(null);
  const [questionAnswerTime, setQuestionAnswerTime] = useState<number>(0);
  const [questionAnswerTimes, setQuestionAnswerTimes] = useState<Record<string, number>>({});

  const currentCategory = categories[currentCategoryIndex];
  const currentQuestion = currentCategory?.questions?.[currentQuestionIndex];

  // Load interview data
  useEffect(() => {
    const loadInterviewData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        
        // Get categories
        const categoriesResponse = await axios.get(
          `https://interviewer.runasp.net/api/submissionInterviews/${id}/categories`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        const categoryIds = categoriesResponse.data;
        
        // Get questions for each category
        const categoryPromises = categoryIds.map((categoryId: string) =>
          axios.get(
            `https://interviewer.runasp.net/api/submissionInterviews/${id}/submissionCategories/${categoryId}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          )
        );
        
        const categoryResponses = await Promise.all(categoryPromises);
        const categoriesData = categoryResponses.map(response => response.data);
        
        setCategories(categoriesData);
        setPhaseStartTime(Date.now());
        setDisplayTimer(readTimeDuration); // Set initial display timer to read time
      } catch (err) {
        console.error('Error loading interview data:', err);
        setError('Failed to load interview data');
      } finally {
        setIsLoading(false);
      }
    };

    loadInterviewData();
  }, [id, readTimeDuration]);

  // Timer effect
  useEffect(() => {
    if (!currentQuestion || !phaseStartTime) return;

    const timer = setInterval(() => {
      const now = Date.now();
      
      if (currentQuestionPhase === 'reading') {
        const remaining = Math.max(0, readTimeDuration * 1000 - (now - phaseStartTime));
        setDisplayTimer(Math.ceil(remaining / 1000));
        
        if (remaining <= 0) {
          setCurrentQuestionPhase('answering');
          setPhaseStartTime(now);
          setQuestionAnswerTime(0); // Reset answer time
          setDisplayTimer(0);
        }
      } else {
        const elapsed = Math.floor((now - phaseStartTime) / 1000);
        setDisplayTimer(elapsed);
        setQuestionAnswerTime(elapsed); // Update answer time
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [currentQuestion, currentQuestionPhase, phaseStartTime, readTimeDuration]);

  const handleAnswer = (questionId: string, answer: string | string[]) => {
    if (currentQuestionPhase !== 'answering') return;
    
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNextQuestion = async () => {
    if (!currentQuestion || currentQuestionPhase === 'reading') return;

    const currentAnswer = answers[currentQuestion.submittedQuestionId];
    
    if (!currentAnswer || 
        (Array.isArray(currentAnswer) && currentAnswer.length === 0) || 
        (typeof currentAnswer === 'string' && currentAnswer.trim() === '')) {
      setShowValidation(true);
      return;
    }

    setShowValidation(false);

    const isLastQuestionInCategory = currentQuestionIndex === currentCategory.questions.length - 1;
    const isLastCategory = currentCategoryIndex === categories.length - 1;

    // Store the current answer time for this question
    setQuestionAnswerTimes(prev => ({
      ...prev,
      [currentQuestion.submittedQuestionId]: questionAnswerTime
    }));

    if (isLastQuestionInCategory) {
      // Submit category answers
      try {
        const token = localStorage.getItem('token');
        
        const submittedQuestions = currentCategory.questions.map(q => {
          const userAnswer = answers[q.submittedQuestionId];
          
          const timeSpent = q.submittedQuestionId === currentQuestion.submittedQuestionId 
            ? questionAnswerTime 
            : (questionAnswerTimes[q.submittedQuestionId] || 0);
          
          if (q.structure === 1) {
            // Text answer (structure 1) - use textAnswer field
            return {
              submittedQuestionId: q.submittedQuestionId,
              textAnswer: userAnswer || '',
              answerIn: timeSpent
            };
          } else {
            // Multiple choice (structure 2 & 3) - use choosenAnswers array
            const answerArray = Array.isArray(userAnswer) ? userAnswer : (userAnswer ? [userAnswer] : []);
            return {
              submittedQuestionId: q.submittedQuestionId,
              choosenAnswers: answerArray.map(ans => ({ answer: ans })),
              answerIn: timeSpent
            };
          }
        });

        // Log what we're sending for this category
        const fullApiPayload = { submittedQuestions };
        
        console.log(`=== SUBMITTING CATEGORY: ${currentCategory.name} ===`);
        console.log('Category ID:', currentCategory.id);
        console.log('Full API Payload Structure:');
        console.log(JSON.stringify(fullApiPayload, null, 2));
        console.log('==========================================');

        await axios.put(
          `https://interviewer.runasp.net/api/submissionInterviews/${id}/submissionCategories/${currentCategory.id}`,
          { submittedQuestions },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (isLastCategory) {
          try {
            const resultsResponse = await axios.get<InterviewResults>(
              `https://interviewer.runasp.net/api/submissionInterviews/${id}/results`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const results: InterviewResults = resultsResponse.data;console.log('Interview results:', results);
            
            navigate({ to: '/results-overview/$id', params: { id } });
            return;
          } catch (resultsError) {
            console.error('Error fetching results:', resultsError);
            setError('Failed to fetch interview results');
            return;
          }
        } else {
          // Move to next category
          console.log('Moving to next category');
          setCurrentCategoryIndex(prev => prev + 1);
          setCurrentQuestionIndex(0);
          
          // Reset phase for next category
          setCurrentQuestionPhase('reading');
          setPhaseStartTime(Date.now());
          setQuestionAnswerTime(0); // Reset answer time
          setDisplayTimer(0);
        }
      } catch (err) {
        console.error('Error submitting category:', err);
        setError('Failed to submit answers');
        return;
      }
    } else {
      // Move to next question
      setCurrentQuestionIndex(prev => prev + 1);
      
      // Reset phase for next question
      setCurrentQuestionPhase('reading');
      setPhaseStartTime(Date.now());
      setQuestionAnswerTime(0); // Reset answer time
      setDisplayTimer(0);
    }
  };

  const isLastQuestion = () => {
    if (!currentCategory) return false;
    return currentQuestionIndex === currentCategory.questions.length - 1 && 
           currentCategoryIndex === categories.length - 1;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !currentQuestion) {
    return <NoData />;
  }

  const renderQuestionInput = () => {
    if (!currentQuestion) return null;

    const currentAnswer = answers[currentQuestion.submittedQuestionId];
    const isInputDisabled = currentQuestionPhase === 'reading';

    switch (currentQuestion.structure) {
      case 1: // Text input
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={(currentAnswer as string) || ''}
            onChange={(e) => handleAnswer(currentQuestion.submittedQuestionId, e.target.value)}
            placeholder="Type your answer here..."
            sx={{ mt: '32px' }}
            error={showValidation && (!currentAnswer || (typeof currentAnswer === 'string' && currentAnswer.trim() === ''))}
            helperText={showValidation && (!currentAnswer || (typeof currentAnswer === 'string' && currentAnswer.trim() === '')) ? "Please provide an answer" : ""}
            disabled={isInputDisabled}
          />
        );
      
      case 2: // Single choice
        return (
          <Box sx={{ mt: '32px' }}>
            {currentQuestion.suggestedAnswers?.map((option, index) => (
              <QuestionOption
                key={index}
                text={option.answer}
                checked={currentAnswer === option.answer}
                onChange={() => handleAnswer(currentQuestion.submittedQuestionId, option.answer)}
                type="radio"
                disabled={isInputDisabled}
              />
            ))}
            {showValidation && !currentAnswer && (
              <Typography color="error" sx={{ mt: 1 }}>Please select an answer</Typography>
            )}
          </Box>
        );
      
      case 3: // Multiple choice
        return (
          <Box sx={{ mt: '32px' }}>
            {currentQuestion.suggestedAnswers?.map((option, index) => {
              const isUserAnswer = Array.isArray(currentAnswer) && currentAnswer.includes(option.answer);

              return (
                <QuestionOption
                  key={index}
                  text={option.answer}
                  checked={isUserAnswer}
                  onChange={() => {
                    const currentAnswers = Array.isArray(currentAnswer) 
                      ? currentAnswer as string[]
                      : [];
                    const newAnswers = currentAnswers.includes(option.answer) 
                      ? currentAnswers.filter((a: string) => a !== option.answer)
                      : [...currentAnswers, option.answer];
                    handleAnswer(currentQuestion.submittedQuestionId, newAnswers);
                  }}
                  type="checkbox"
                  disabled={isInputDisabled}
                />
              );
            })}
            {showValidation && (!currentAnswer || (currentAnswer as string[]).length === 0) && (
              <Typography color="error" sx={{ mt: 1 }}>Please select at least one answer</Typography>
            )}
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', background: '#f9f9f9', minHeight: '100vh', padding: {xs: '20px 16px', md: '125px 75px'} }}>
      <StyledContainer>
        {currentCategory && (
            <Typography variant='h1' sx={{ color: 'var(--Interviewer-Black)', fontSize: '32px'}}>
              {currentCategory.name}
            </Typography>
        )}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row-reverse' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          width: '100%',
          gap: { xs: '16px', md: 'auto' },
        }}>
          {currentQuestion && (
            <TimerDisplay 
              phase={currentQuestionPhase}
              displayTime={displayTimer}
              readTimeDuration={readTimeDuration}
            />
          )}
          <Typography variant='h2'>
            Question {currentQuestionIndex + 1}
          </Typography>
        </Box>
        <Box sx={{mt: '24px'}}>
          <Typography variant='body1'>
            {currentQuestion.text}
          </Typography>
          {renderQuestionInput()}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{mt: '32px'}}
            onClick={handleNextQuestion}
            disabled={currentQuestionPhase === 'reading'}
          >
            {isLastQuestion() ? 'Finish Interview' : 'Next Question'}
          </Button>
        </Box>
      </StyledContainer>
    </Box>
  )
}



  